export type SkillScope = 'personal' | 'shared'

export interface SkillItem {
  id: string
  title: string
  description?: string
  content?: string
  scope: SkillScope
  updatedAt?: string
}

/**
 * Falls back to a placeholder when a skill has no title (FHIR title is optional).
 * @param skill - Skill to derive a display title from.
 * @returns The skill's title, or a placeholder when it is empty.
 */
export function skillItemTitle(skill: Pick<SkillItem, 'title'>): string {
  return skill.title || 'Untitled skill'
}

export interface SkillEditorValue {
  title: string
  description: string
  content: string
  scope: SkillScope
}

export interface SkillMetadata {
  title: string
  description: string
}

/* `onPrimary` is for chips sitting on the filled user-message bubble. */
export type SkillTone = 'default' | 'onPrimary'

export const SCOPE_LABEL: Record<SkillScope, string> = {
  personal: 'Personal',
  shared: 'Workspace',
}

export const SCOPE_ORDER: SkillScope[] = ['personal', 'shared']

/**
 * Picker grouping. "mine" pulls a skill out of its scope bucket so the current
 * user's own skills surface together at the top regardless of personal/shared.
 */
export type SkillGroup = 'mine' | SkillScope

export const GROUP_LABEL: Record<SkillGroup, string> = {
  mine: 'My Skills',
  personal: 'Personal',
  shared: 'Team Skills',
}

export const GROUP_ORDER: SkillGroup[] = ['mine', 'personal', 'shared']

// Render order (My skills, Personal, Workspace), so keyboard nav maps to a
// single moving index across the grouped sections. Own skills are pulled into
// the "mine" bucket and excluded from their scope bucket so a user never sees
// the same skill listed twice.
export function groupSkillItems<TSkill extends SkillItem>(
  skills: readonly TSkill[],
  isOwnSkill?: (skill: TSkill) => boolean
) {
  return GROUP_ORDER.flatMap((group) => {
    const items = skills.filter((skill) => {
      const mine = isOwnSkill?.(skill) ?? false
      if (group === 'mine') {
        return mine
      }
      return !mine && skill.scope === group
    })
    return items.length > 0 ? [{ group, items }] : []
  })
}

/**
 * The skills in the order the picker lists them, for mapping an active index
 * back to a skill.
 * @param skills - Skills to order.
 * @param isOwnSkill - Marks the current user's own skills, which list first.
 * @returns The skills in list order.
 */
export function orderSkillItems<TSkill extends SkillItem>(
  skills: readonly TSkill[],
  isOwnSkill?: (skill: TSkill) => boolean
): TSkill[] {
  return groupSkillItems(skills, isOwnSkill).flatMap((group) => group.items)
}
